<template lang="pug">
  .gl-settings.gl-settings-users
    // nav
    gl-nav

    // page
    .gl-settings__page.gl-layout--medium-width(layout="row")

      // sidenav
      gl-settings-sidenav

      // content
      .gl-settings__page__content(flex)

        // head
        .gl-settings__head
          .gl-settings__head__context App Settings
          .gl-settings__head__tsb(layout="row" layout-align="start space-between")
            .gl-settings__head__title Users
            .gl-settings__head__tsb__search(flex)
              font-awesome-icon(icon="search")
              .gl-settings__head__tsb__search__input
                b-form-input(
                  v-model.trim="query.users"
                  v-gl-focus="true"
                  required
                  type="text"
                  autocomplete="off"
                  placeholder="Search users by name or email")
            .gl-settings__head__tsb__search__btn
              gl-btn(:options="{ label: 'Invite user', type: 'button', style: 'brand-settings' }" @click.native="showModal({ type: 'invite' })")

        // site settings
        .gl-layout__segment(layout="column" layout-gt-md="row")
          .gl-layout__segment__meta
            .gl-layout__segment__title All Users
            .gl-layout__segment__description Manage the users that have access to your app and control what they can access.
          .gl-layout__segment__content(flex)

            // all users
            .gl-settings-users__all

              // loading
              gl-loading-card(:options="{ message: 'Loading users', border: 'none' }" v-if="isLoading.getUsers && !queryUsers.length")

              // 404
              .gl-settings-users__all__404(v-if="!isLoading.getUsers && !queryUsers.length") No users found

              // user
              .gl-settings-users__all__user(v-for="user in queryUsers" :key="user.$key" v-bind:class="{ 'inactive': user.status === 'Inactive' }")
                .gl-settings-users__all__user__top(layout="row" layout-align="space-between start")
                  .gl-settings-users__all__user__avatar
                    img(:src="user.email | gravatar")
                  .gl-settings-users__all__user__meta(flex)
                    .gl-settings-users__all__user__meta__name {{ user.name }}
                    .gl-settings-users__all__user__meta__email {{ user.email }}
                  .gl-settings-users__all__user__btns(layout="row" layout-align="end start")
                    gl-btn.mr-12(v-if="user.status !== 'Inactive'" :options="{ label: 'Edit', type: 'button', style: 'brand-outline' }" @click.native="showModal({ type: 'edit', user })")
                    gl-btn(v-if="user.status !== 'Inactive'" :options="{ label: 'Deactivate', type: 'button', style: 'danger-outline' }" @click.native="showModal({ type: 'deactivate', user })")
                    gl-btn(v-if="user.status === 'Inactive'" :options="{ label: 'Activate', type: 'button', style: 'success-outline' }" @click.native="showModal({ type: 'activate', user })")
                .gl-settings-users__all__user__bottom(layout="row" layout-align="start start")
                  .gl-settings-users__all__user__lv
                    .gl-settings-users__all__user__lv__label STATUS
                    .gl-settings-users__all__user__lv__value {{ user.status }}
                  .gl-settings-users__all__user__lv
                    .gl-settings-users__all__user__lv__label ROLE
                    .gl-settings-users__all__user__lv__value.cap {{ user.role }}
                  .gl-settings-users__all__user__lv
                    .gl-settings-users__all__user__lv__label GROUPS
                    .gl-settings-users__all__user__lv__value
                      span.pr-4 {{ user.groups | groupsStart }}
                      span(v-if="user.groups.length > 1")
                        span.gl-settings-users__all__user__lv__value__lite(:id="'groups-end-' + user.id") {{ user.groups | groupsEnd }}
                        b-tooltip(:target="'groups-end-' + user.id") {{ user.groups | groupsTooltip }}

            // inactive toggle
            .gl-layout__segment__content__toggle(v-if="users.inactive.length" @click="showInactiveUsers = !showInactiveUsers")
              span {{ showInactiveUsers ? 'Hide': 'Show' }} inactive users ({{ users.inactive.length }} inactive)

    // modal
    b-modal(:title="modal.config ? modal.config.title : null" :hide-footer="true" ref="userModal" size="md")
      // activate/deactivate message
      b-alert.mb-0(show v-if="modal.type === 'activate' || modal.type === 'deactivate'" variant="warning")
        span(v-if="modal.type === 'activate'") Activate {{ modal.user.name }} for all groups?
        span(v-if="modal.type === 'deactivate'") Deactivate {{ modal.user.name }} from all groups?

      // form
      b-form(v-if="modal.config" @submit.prevent="saveModal" novalidate)
        // form wrapper
        div(v-if="modal.config.showForm")
          // alerts
          gl-alert(:options="{ errorList }" @clear-error-list="errorList = []")

          div(layout="row" layout-align="space-between start")

            // First Name
            b-form-group.mr-12(:state="form.firstName.state" label="First Name" label-for="input--first-name" flex)
              b-form-input(
                :state="form.firstName.state"
                v-model.trim="form.firstName.val"
                required
                id="input--first-name"
                type="text")
              .invalid-feedback(v-if="form.firstName.invalidFeedback") {{ form.email.invalidFeedback }}

            // Last Name
            b-form-group.ml-12(:state="form.lastName.state" label="Last Name" label-for="lastName" flex)
              b-form-input(
                :state="form.lastName.state"
                v-model.trim="form.lastName.val"
                required
                id="lastName"
                type="text")
              .invalid-feedback(v-if="form.lastName.invalidFeedback") {{ form.lastName.invalidFeedback }}

          // Email
          b-form-group(:state="form.email.state" label="Email" label-for="email" flex)
            b-form-input(
              :state="form.email.state"
              v-model.trim="form.email.val"
              required
              id="email"
              type="text")
            .invalid-feedback(v-if="form.email.invalidFeedback") {{ form.email.invalidFeedback }}

          // Role
          b-form-group(:state="form.role.state" label="Role" label-for="role")
            b-form-select(
              :state="form.role.state"
              :options="roles"
              v-model.trim="form.role.val"
              required
              id="role")
            .invalid-feedback(v-if="form.role.invalidFeedback") {{ form.role.invalidFeedback }}

          b-alert.mb-0(show v-if="modal.user && modal.user.status && modal.user.status === 'Pending'" variant="warning") Pending users may not be added to groups

          div(v-if="!modal.user || !modal.user.status || modal.user.status !== 'Pending'")
            // Groups: input
            gl-autocomplete(
              :list="allGroups"
              :options="{ label: 'Groups', placeholder: 'Search groups by name', state: form.groups.state, invalidFeedback: form.groups.invalidFeedback, listLoading: isLoading.getGroups }"
              @item-clicked="(group) => { tempAddGroupToUser({ group, user: modal.user }) }")

            // Groups: current
            .gl-grid__wrapper(v-if="form.groups.val.length")
              .gl-grid__wrapper__row(v-for="group in form.groups.val" layout="row" layout-align="space-between center")
                .gl-grid__wrapper__row__img
                  gl-group-logo(:group-id="group.id")
                .gl-grid__wrapper__row__name(flex)
                  span(:class="{ 'pr-6': group.isActive === false }") {{ group.name }}
                  b-badge(v-if="group.isActive === false") Inactive
                .gl-grid__wrapper__row__action(@click="tempRemoveGroupFromUser({ group, user: modal.user })") Remove user from this group

        // footer
        .modal-footer(v-if="modal.config.saveBtn")
          gl-btn.mr-12(:options="{ label: 'Cancel', type: 'button', style: 'outline' }" @click.native="hideModal()")
          gl-btn(:options="{ label: modal.config.saveBtn.label, type: 'submit', style: modal.config.saveBtn.style, isLoading: isLoading.saveUser }")

</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

export default {

  // data
  data () {
    return {
      query: {
        users: '',
        companies: ''
      },
      users: {
        all: [],
        active: [],
        inactive: [],
        pending: [],
        visible: []
      },
      allGroups: [],
      errorList: [],
      popoverIsOpen: false,
      showInactiveUsers: false,
      isLoading: {
        getUsers: true,
        saveUser: false,
        getGroups: false
      },
      roles: [
        { value: '', text: 'Select an role', disabled: true },
        { value: 'admin', text: 'App Admin' },
        { value: 'manager', text: 'Manager' },
        { value: 'basic', text: 'Basic' }
      ],
      form: {
        id: { val: '', state: null, invalidFeedback: '' },
        firstName: { val: '', state: null, invalidFeedback: '' },
        lastName: { val: '', state: null, invalidFeedback: '' },
        email: { val: '', state: null, invalidFeedback: '' },
        role: { val: '', state: null, invalidFeedback: '' },
        groups: { val: [], state: null, invalidFeedback: '' },
        addedGroups: { val: [], state: null, invalidFeedback: '' },
        removedGroups: { val: [], state: null, invalidFeedback: '' }
      },
      modal: {
        type: '',
        user: null,
        config: null
      },
      MODAL_CONFIG: {
        edit: {
          title: 'Edit User',
          showForm: true,
          saveEndpoint: {
            url: 'user/update',
            type: 'post'
          },
          saveBtn: {
            label: 'Save',
            style: 'brand-settings'
          },
          successToast: 'User settings saved',
          focus: '#input--first-name'
        },
        invite: {
          title: 'Invite User',
          showForm: true,
          saveEndpoint: {
            url: 'user/invite',
            type: 'post'
          },
          saveBtn: {
            label: 'Send invitation',
            style: 'brand-settings'
          },
          successToast: 'User invite sent',
          focus: '#input--first-name'
        },
        deactivate: {
          title: 'Deactivate User',
          showForm: false,
          saveEndpoint: {
            url: 'user/update',
            type: 'post'
          },
          saveBtn: {
            label: 'Deactivate',
            style: 'danger-outline'
          },
          successToast: 'User deactivated'
        },
        activate: {
          title: 'Activate User',
          showForm: false,
          saveEndpoint: {
            url: 'user/update',
            type: 'post'
          },
          saveBtn: {
            label: 'Activate',
            style: 'success-outline'
          },
          successToast: 'User activated'
        }
      }
    }
  },

  // on mount
  mounted () {
    // get fresh users
    this.getUsers()
  },

  // computed
  computed: {
    ...mapGetters(['groups', 'config']),
    queryUsers () {
      // determine userList based on showInactiveUsers
      let userList = this.showInactiveUsers ? this.users.all : this.users.visible

      // add $key to each item in the list (for v-for)
      let keyList = _.map(userList, (item) => {
        return { ...item, $key: _.uniqueId('item-') }
      })

      // filter keyList
      return _.filter(keyList, (user) => {
        // define query + user attributes
        let query = this.query.users.toLowerCase()
        let firstName = user.firstName.toLowerCase()
        let lastName = user.lastName.toLowerCase()
        let fullName = user.name.toLowerCase()
        let email = user.email.toLowerCase()

        // return true user if query matches any user attributes
        return _.includes(firstName, query) || _.includes(lastName, query) || _.includes(email, query) || _.includes(fullName, query)
      })
    }
  },

  // filters
  filters: {
    groupsStart: function (value) {
      // if group has atleast one value
      if (_.isArray(value) && _.isObject(value[0])) {
        // return the name of the group
        return value[0].name
      }
    },
    groupsEnd: function (value) {
      // if value is an array
      if (_.isArray(value)) {
        // if value is 2 or more -> return message
        if (value.length === 2) {
          return 'and ' + (value.length - 1) + ' other'
        } else if (value.length > 2) {
          return 'and ' + (value.length - 1) + ' others'
        }
      }
    },
    groupsTooltip: function (value) {
      // if groups is an array
      if (_.isArray(value)) {
        // display group names seperated by a comma
        return value.map(group => group.name).join(', ')
      }
    }
  },

  // methods
  methods: {
    getUsers () {
      // start loading
      this.isLoading.getUsers = true

      // get users
      return this.$glAuth.getUsers()
        .then(r => {
          // bind active users
          this.users.active = _.sortBy(_.filter(r, { status: 'Active' }), 'firstName')

          // bind pending users
          this.users.pending = _.sortBy(_.filter(r, { status: 'Pending' }), 'firstName')

          // bind inactive users
          this.users.inactive = _.sortBy(_.filter(r, { status: 'Inactive' }), 'firstName')

          // bind visible (not inactive) users
          this.users.visible = _.sortBy(_.concat(this.users.active, this.users.pending), 'firstName')

          // bind all users -> active/pending users b4 inactive users
          this.users.all = _.concat(this.users.visible, this.users.inactive)
        })
        .catch(e => {
          // log error
          console.log(e)
        })
        .finally(r => {
          // stop loading
          this.isLoading.getUsers = false
        })
    },
    getGroups () {
      // start loading
      this.isLoading.getGroups = true

      // get fresh groups for the userModal
      this.$glAuth.getAllGroups()
        .then(r => {
          this.allGroups = r
        })
        .catch(e => {
          // log error
          console.log(e)
        })
        .finally(r => {
          // stop loading
          this.isLoading.getGroups = false
        })
    },
    showModal (options) {
      // clear previous errors
      this.errorList = []

      // clear previous feedback
      this.$glForm.clearFeedback(this.form)

      // bind modal.type
      this.modal.type = options.type

      // bind modal.config
      this.modal.config = this.MODAL_CONFIG[this.modal.type]

      // get fresh groups
      this.getGroups()

      // open modal
      this.$refs.userModal.show()

      // if config requests focus -> focus input
      if (this.modal.config.focus) {
        this.$glDom.focus({ css: this.modal.config.focus })
      }

      // if: modal type is invite -> reset form
      if (this.modal.type === 'invite') this.bindForm({ reset: true })

      // else: bind form with passed in user
      else this.bindForm({ user: options.user })
    },
    hideModal () {
      // reset form
      this.$glForm.reset(this.form)

      // hide user modal
      this.$refs.userModal.hide()
    },
    bindForm (options) {
      // if: user defined
      if (_.isObject(options.user)) {
        // bind form w/ user
        this.form.id.val = options.user.id
        this.form.firstName.val = options.user.firstName
        this.form.lastName.val = options.user.lastName
        this.form.email.val = options.user.email
        this.form.role.val = options.user.role
        this.form.groups.val = [ ...options.user.groups ]

        // bind modal w/ user
        this.modal.user = options.user
      }

      // if: a form reset is requested
      if (options.reset) {
        // reset form
        this.$glForm.reset(this.form)
      }
    },
    saveModal () {
      // if: type is invite or edit -> inviteEditUser
      if (this.modal.type === 'invite' || this.modal.type === 'edit') this.inviteEditUser()

      // if: type is deactivate -> deactivateUser
      else if (this.modal.type === 'deactivate') this.toggleUser({ isActive: false })

      // else: activateUser
      else this.toggleUser({ isActive: true })
    },
    inviteEditUser () {
      // clear previous feedback
      this.$glForm.clearFeedback(this.form)

      // if form is valid
      if (this.validateInviteEdit()) {
        // start loading
        this.isLoading.saveUser = true

        // init request object
        let request = {
          firstName: this.form.firstName.val,
          lastName: this.form.lastName.val,
          email: this.form.email.val,
          role: this.form.role.val
        }

        // if invite -> add groups to request
        if (this.modal.type === 'invite') {
          request.groups = _.map(this.form.groups.val, 'id')
        }

        // if edit
        if (this.modal.type === 'edit') {
          // add id to the request
          request.id = this.form.id.val

          // add addedGroups to request
          request.addedGroups = _.map(this.form.addedGroups.val, (x) => {
            return { userId: x.user.id, groupId: x.group.id }
          })

          // add removedGroups to request
          request.removedGroups = _.map(this.form.removedGroups.val, (x) => {
            return { userId: x.user.id, groupId: x.group.id }
          })
        }

        // set data
        return this.$glHttp[this.modal.config.saveEndpoint.type](this.modal.config.saveEndpoint.url, request)
          .then(() => {
            // get fresh users
            return this.getUsers()
          })
          .then(() => {
            // build successMessage based on isActive
            let successMessage = (this.modal.type === 'invite') ? `Email invite sent to ${this.form.email.val}` : `${this.form.firstName.val} ${this.form.lastName.val} settings saved`

            // show success notification
            this.$toasted.show(successMessage, {
              theme: 'primary',
              type: 'success',
              position: 'top-center',
              duration: 5000
            })

            // hide modal
            this.hideModal()
          })
          .catch(e => {
            // push server error
            this.errorList.push(this.$glForm.parseError(e))
          })
          .finally(() => {
            // stop loading
            this.isLoading.saveUser = false
          })
      } else {
        // focus first error
        this.$glForm.focusFirstError({ location: 'modal' })
      }
    },
    toggleUser (options) {
      // start loading
      this.isLoading.saveUser = true

      // build request
      const REQUEST = {
        id: this.modal.user.id,
        isActive: options.isActive
      }

      // send request
      return this.$glHttp[this.modal.config.saveEndpoint.type](this.modal.config.saveEndpoint.url, REQUEST)
        .then(() => {
          // get fresh users
          return this.getUsers()
        })
        .then(() => {
          // build successMessage based on isActive
          let successMessage = (options.isActive) ? `${this.modal.user.name} is now active` : `${this.modal.user.name} is now inactivate`

          // show success notification
          this.$toasted.show(successMessage, {
            theme: 'primary',
            type: 'success',
            position: 'top-center',
            duration: 5000
          })

          // hide modal
          this.hideModal()
        })
        // stop loading
        .finally(() => { this.isLoading.saveUser = false })
    },
    validateInviteEdit () {
      // if no firstName -> invalid
      if (!this.form.firstName.val) {
        this.form.firstName.state = 'invalid'
        this.form.firstName.invalidFeedback = 'A first name is required'
      }
      // if no lastName -> invalid
      if (!this.form.lastName.val) {
        this.form.lastName.state = 'invalid'
        this.form.lastName.invalidFeedback = 'A last name is required'
      }
      // if no email -> invalid
      if (!this.form.email.val) {
        this.form.email.state = 'invalid'
        this.form.email.invalidFeedback = 'An email is required'
      }
      // if email does not have an @ -> invalid
      if (this.form.email.val && !this.form.email.val.match(/\S+@\S+/)) {
        this.form.email.state = 'invalid'
        this.form.email.invalidFeedback = 'A valid email address is required'
      }
      // if no role -> invalid
      if (!this.form.role.val) {
        this.form.role.state = 'invalid'
        this.form.role.invalidFeedback = 'A role is required'
      }
      // if no groups -> invalid
      if (!this.form.groups.val.length) {
        this.form.groups.state = 'invalid'
        this.form.groups.invalidFeedback = 'Atleast one group is required'
      }

      // return true if no fields are invalid
      return this.$glForm.hasNoInvalid(this.form)
    },
    tempAddGroupToUser (options) {
      // push group onto list
      this.form.groups.val.push(options.group)

      // remove duplicates from list + sort by name
      this.form.groups.val = _.sortBy(_.uniqBy(this.form.groups.val, 'id'))

      // if edit -> add group to addedGroups
      if (this.modal.type === 'edit') {
        // push remove group onto the list
        this.form.addedGroups.val.push({
          user: options.user,
          group: options.group
        })

        // ensure addedGroups does not have duplicate groups + sort
        this.form.addedGroups.val = _.sortBy(_.uniqBy(this.form.addedGroups.val, (x) => x.group.id), 'group.id')
      }
    },
    tempRemoveGroupFromUser (options) {
      // remove the matching group from the list
      this.form.groups.val = _.filter(this.form.groups.val, (val) => {
        return val.id !== options.group.id
      })

      // if edit -> add group to removedGroups
      if (this.modal.type === 'edit') {
        // push remove group onto the list
        this.form.removedGroups.val.push({
          user: options.user,
          group: options.group
        })

        // ensure removedGroups does not have duplicates groups + sort
        this.form.removedGroups.val = _.sortBy(_.uniqBy(this.form.removedGroups.val, (x) => x.group.id), 'group.id')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '../styles/colors';
  @import '../styles/variables';

  .gl-settings-users {

    &__all {
      margin-top: $gl-gutter;
      background-color: $gl-white;
      border: 1px solid $gl-grey-border;
      border-radius: 3px;

      &__loading, &__404 {
        padding: $gl-gutter;
      }

      &__404 {
        color: $gl-grey;
        font-size: 18px;
      }

      &__user {
        padding: $gl-gutter;
        border-bottom: 1px solid $gl-grey-border;
        &:last-child {
          border-bottom: none;
        }
        &.inactive {
          background-color: $gl-grey-smoke;
        }

        &__top {
          margin-bottom: 12px;
        }

        &__avatar {
          width: 40px;
          height: 40px;
          border-radius: 2px;
          margin-right: $gl-gutter;
          box-shadow: $gl-box-shadow-light;

          img {
            width: auto;
            max-width: 100%;
          }
        }

        &__meta {

          &__name {
            font-weight: 700;
            color: $gl-grey-darker;
            margin-bottom: 8px;
          }

          &__email {
            font-size: 15px;
            font-weight: 400;
          }
        }

        &__lv {
          font-size: 14px;
          font-weight: 400;
          margin-right: 40px;
          &:last-child {
            margin-right: 0;
          }

          &__label {
            color: #8DA2B5;
            margin-bottom: 6px;
          }

          &__value {
            color: $gl-grey-darker;
            &.cap {
              text-transform: capitalize;
            }

            &__lite {
              color: #8DA2B5;
            }
          }
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/views/SettingsUsers.vue